/**
 * Poser une question à l'agent.
 *
 * L'historique envoyé est celui du panneau, borné aux douze derniers tours
 * comme l'exige le noyau. Après chaque réponse, tout est rafraîchi : l'agent
 * a pu écrire n'importe où, et une liste qui ne montrerait pas l'affectation
 * qu'il vient de poser ferait croire qu'il a menti.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DomainError } from "@dker/core";
import { agentServices, serviceDeps, useExecutionContext } from "@/services";
import { useAgentStore } from "@/stores/agentStore";

const HISTORIQUE = 12;

function toMessage(error: unknown): string {
  if (error instanceof DomainError) return error.message;
  return "L'agent n'a pas pu répondre. Réessayez dans un instant.";
}

export function useAskAgent() {
  const qc = useQueryClient();
  const makeContext = useExecutionContext();
  const push = useAgentStore((s) => s.push);

  return useMutation({
    mutationFn: async (question: string) => {
      const history = useAgentStore
        .getState()
        .turns.filter((t) => !t.failed)
        .slice(-HISTORIQUE)
        .map((t) => ({ role: t.role, text: t.text }));
      push({ role: "patron", text: question });
      return agentServices.demanderAgent(makeContext(), { question, history }, serviceDeps);
    },
    onSuccess: (r) => {
      push({
        role: "agent",
        text: r.answer,
        writes: r.writes,
        proposals: r.proposals,
        toolsUsed: r.toolsUsed,
      });
      if (r.writes.length || r.proposals.length) void qc.invalidateQueries();
    },
    onError: (error) => push({ role: "agent", text: toMessage(error), failed: true }),
  });
}
